/* eslint-disable functional/immutable-data -- Canvas の描画状態はこのシーンが所有し、dispose で手放す。 */
import {
  BRICK_COLORS,
  BRICK_RATIO,
  type LaidBrick,
  layoutBrickText,
  STUD_RATIO,
  shade,
} from "./brick-font"

/**
 * トップのオープニング。
 * 1. ブロックが下の段から降ってきて「YOSHINANI」を組み上げる
 * 2. 最後に青い「.」のブロックがはまり、土台のスタッドに波紋が広がる
 * 3. はまった点から円形に抜けて、下のヒーローが現れる
 */

const BACKGROUND = "#f4f3eb"
const STUD = "#e7e4d6"
const STUD_EDGE = "#d9d5c3"
const PALETTE = [
  BRICK_COLORS.yellow,
  BRICK_COLORS.red,
  BRICK_COLORS.blue,
  BRICK_COLORS.ink,
  BRICK_COLORS.ink,
]

/** 各段の落下開始の間隔・1 個の落下時間・「.」の落下・波紋・抜け（ms） */
const ROW_STAGGER = 120
const FALL = 460
const FINAL_FALL = 380
const HOLD_AFTER_IMPACT = 520
const EXIT = 640
const QUICK_EXIT = 360

interface PlacedBrick extends LaidBrick {
  start: number
  x: number
  y: number
  width: number
  centerX: number
  centerY: number
}

export type OpeningPhase = "build" | "exit" | "done"

export interface OpeningScene {
  /** 1 フレーム描く。組み上げ中・抜け中・終了のどれかを返す。 */
  frame: (now: number) => OpeningPhase
  /** 途中で飛ばす。すぐ抜けの演出に入る。 */
  skip: () => void
  /** 「.」がはまった点（画面座標）。 */
  impactPoint: () => { x: number; y: number }
  resize: () => void
}

function easeOutBack(t: number): number {
  const c = 1.5
  return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2
}

function easeInCubic(t: number): number {
  return t * t * t
}

function drawBrick(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  span: number,
  unit: number,
  color: string,
): void {
  const height = unit * BRICK_RATIO
  const width = span * unit
  const stud = unit * STUD_RATIO
  // スタッド
  ctx.fillStyle = shade(color, -0.08)
  for (let i = 0; i < span; i += 1) {
    ctx.fillRect(x + i * unit + unit * 0.2, y - stud, unit * 0.6, stud + 1)
  }
  ctx.fillStyle = shade(color, 0.3)
  for (let i = 0; i < span; i += 1) {
    ctx.fillRect(x + i * unit + unit * 0.22, y - stud, unit * 0.12, stud * 0.9)
  }
  // 本体
  ctx.fillStyle = color
  ctx.fillRect(x, y, width, height)
  ctx.fillStyle = shade(color, 0.28)
  ctx.fillRect(x, y, width, Math.max(1, unit * 0.08))
  ctx.fillStyle = shade(color, -0.22)
  ctx.fillRect(x, y + height - unit * 0.12, width, unit * 0.12)
  ctx.fillRect(x + width - Math.max(1, unit * 0.06), y, unit * 0.06, height)
}

export function createOpeningScene(canvas: HTMLCanvasElement): OpeningScene {
  const context = canvas.getContext("2d")
  if (!context) throw new Error("2D canvas is not available")
  const ctx = context
  let width = 1
  let height = 1
  let unit = 10
  let studGap = 24
  let bricks: PlacedBrick[] = []
  let finalStart = 0
  let impactAt = 0
  let exitAt = 0
  let exitDuration = EXIT
  let origin: number | null = null
  let skipRequested = false
  let impact = { x: 0, y: 0 }
  let maxRadius = 1

  const layout = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    width = canvas.clientWidth
    height = canvas.clientHeight
    canvas.width = Math.round(width * dpr)
    canvas.height = Math.round(height * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    const narrow = width < 640
    const text = layoutBrickText(
      narrow ? ["YOSHI", "NANI."] : ["YOSHINANI."],
      PALETTE,
      11,
    )
    unit = Math.min(
      (width * (narrow ? 0.8 : 0.82)) / text.cols,
      (height * 0.46) / (text.rows * BRICK_RATIO),
      34,
    )
    studGap = narrow ? 22 : 28
    const left = (width - text.cols * unit) / 2
    const top = (height - text.rows * unit * BRICK_RATIO) / 2
    const maxRow = text.rows - 1
    // 下の段から順に落とす。同じ段は左からわずかにずらす。
    const stagger = narrow ? ROW_STAGGER * 0.55 : ROW_STAGGER
    bricks = text.bricks
      .map((brick) => {
        const x = left + brick.col * unit
        const y = top + brick.row * unit * BRICK_RATIO
        return {
          ...brick,
          start: (maxRow - brick.row) * stagger + brick.col * 7,
          x,
          y,
          width: brick.span * unit,
          centerX: x + (brick.span * unit) / 2,
          centerY: y + (unit * BRICK_RATIO) / 2,
        }
      })
      // 下の段を先に描き、上の段がスタッドを覆うようにする
      .sort((a, b) => b.row - a.row)
    const regular = bricks.filter((brick) => !brick.final)
    const finals = bricks.filter((brick) => brick.final)
    finalStart = Math.max(...regular.map((brick) => brick.start)) + FALL + 120
    impactAt = finalStart + FINAL_FALL
    exitAt = impactAt + HOLD_AFTER_IMPACT
    const finalTop = Math.min(...finals.map((brick) => brick.y))
    const finalBottom = Math.max(
      ...finals.map((brick) => brick.y + unit * BRICK_RATIO),
    )
    impact = {
      x:
        finals.reduce((sum, brick) => sum + brick.centerX, 0) /
        Math.max(1, finals.length),
      y: (finalTop + finalBottom) / 2,
    }
    maxRadius = Math.hypot(
      Math.max(impact.x, width - impact.x),
      Math.max(impact.y, height - impact.y),
    )
  }

  const drawStuds = (elapsed: number) => {
    const sinceImpact = elapsed - impactAt
    const radius = sinceImpact * 0.9
    const ripple = sinceImpact > 0 && sinceImpact < 1400
    const r = studGap * 0.2
    for (let y = studGap / 2; y < height; y += studGap) {
      for (let x = studGap / 2; x < width; x += studGap) {
        let lift = 0
        if (ripple) {
          const band = (Math.hypot(x - impact.x, y - impact.y) - radius) / 46
          lift = Math.exp(-band * band) * Math.max(0, 1 - sinceImpact / 1400)
        }
        if (lift > 0.04) {
          ctx.fillStyle = shade(BRICK_COLORS.yellow, -0.15 * (1 - lift))
          ctx.globalAlpha = Math.min(1, 0.25 + lift)
          ctx.beginPath()
          ctx.arc(x, y - lift * 3, r * (1 + lift * 0.35), 0, Math.PI * 2)
          ctx.fill()
          ctx.globalAlpha = 1
        } else {
          ctx.fillStyle = STUD_EDGE
          ctx.beginPath()
          ctx.arc(x + 0.8, y + 1, r, 0, Math.PI * 2)
          ctx.fill()
          ctx.fillStyle = STUD
          ctx.beginPath()
          ctx.arc(x, y, r, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }
  }

  const draw = (elapsed: number) => {
    ctx.globalCompositeOperation = "source-over"
    ctx.clearRect(0, 0, width, height)
    ctx.fillStyle = BACKGROUND
    ctx.fillRect(0, 0, width, height)
    drawStuds(elapsed)
    const sinceImpact = elapsed - impactAt
    for (const brick of bricks) {
      const start = brick.final ? finalStart : brick.start
      const duration = brick.final ? FINAL_FALL : FALL
      const t = (elapsed - start) / duration
      if (t <= 0) continue
      const drop = brick.final
        ? (1 - easeInCubic(Math.min(1, t))) * (brick.y + unit * 4)
        : (1 - easeOutBack(Math.min(1, t))) * Math.min(height * 0.55, 420)
      // 波紋が通るとブロックが小さく跳ねる
      let hop = 0
      if (sinceImpact > 0 && !brick.final) {
        const distance = Math.hypot(
          brick.centerX - impact.x,
          brick.centerY - impact.y,
        )
        const band = (distance - sinceImpact * 0.9) / (unit * 2.2)
        hop =
          Math.exp(-band * band) *
          unit *
          0.45 *
          Math.max(0, 1 - sinceImpact / 1100)
      }
      ctx.globalAlpha = brick.final ? 1 : Math.min(1, t * 3)
      drawBrick(
        ctx,
        brick.x,
        brick.y - drop - hop,
        brick.span,
        unit,
        brick.color,
      )
    }
    ctx.globalAlpha = 1
    // 着地の一瞬だけ「.」の周りに白い輪を出す
    if (sinceImpact > 0 && sinceImpact < 420) {
      const p = sinceImpact / 420
      ctx.strokeStyle = `rgb(255 255 255 / ${1 - p})`
      ctx.lineWidth = unit * 0.35 * (1 - p) + 1
      ctx.beginPath()
      ctx.arc(impact.x, impact.y, unit * (1.4 + p * 4), 0, Math.PI * 2)
      ctx.stroke()
    }
    if (elapsed > exitAt) {
      const p = Math.min(1, (elapsed - exitAt) / exitDuration)
      ctx.globalCompositeOperation = "destination-out"
      ctx.beginPath()
      ctx.arc(
        impact.x,
        impact.y,
        easeInCubic(p) * maxRadius * 1.02,
        0,
        Math.PI * 2,
      )
      ctx.fill()
      ctx.globalCompositeOperation = "source-over"
    }
  }

  layout()

  return {
    frame(now) {
      origin ??= now
      // 飛ばす指示は次のフレームの時刻で反映する（rAF と performance.now の時計を混ぜない）
      if (skipRequested && now - origin < exitAt) {
        exitDuration = QUICK_EXIT
        origin = now - exitAt
      }
      skipRequested = false
      const elapsed = now - origin
      draw(elapsed)
      if (elapsed >= exitAt + exitDuration) return "done"
      return elapsed >= exitAt ? "exit" : "build"
    },
    skip() {
      skipRequested = true
    },
    impactPoint: () => impact,
    resize: layout,
  }
}
